import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import { Combobox, type ComboboxOption } from '../product/combobox';
import type { ProductDetail, ProductListItem } from '../product/types';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { StockCountDetail, StockCountLineItem, StockCountReview } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

async function searchProducts(query: string): Promise<ComboboxOption[]> {
  if (!query.trim()) return [];
  const res = await apiFetch<{ items: ProductListItem[] }>(
    `/products?limit=8&query=${encodeURIComponent(query)}`,
  );
  return res.items.map((p) => ({ id: p.id, label: `${p.name} (${p.sku})` }));
}

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

/**
 * 盘点详情页（S13）。同一路由按 status 切三种模式：录入
 * （DRAFT/IN_PROGRESS）、审批（PENDING_APPROVAL，D-08 的核心）、
 * 只读小结（APPROVED/REJECTED/CANCELLED）。
 */
export function StockCountDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const countId = id!;
  const { t } = useTranslation();

  const { data: detail, isPending } = useQuery({
    queryKey: ['stock-counts', 'detail', countId],
    queryFn: () => apiFetch<StockCountDetail>(`/stock-counts/${countId}`),
  });

  if (isPending || !detail)
    return (
      <div style={{ maxWidth: '960px' }}>
        <SkeletonBlock height={64} />
        <SkeletonRows rows={6} />
      </div>
    );

  return (
    <div style={{ maxWidth: '960px' }}>
      <Link to="/stock-counts" className="field-hint">
        {t('stockCount.backToList')}
      </Link>
      <div className="row row--between" style={{ marginTop: 'var(--space-2)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{detail.docNo}</h1>
        <span className={detail.status === 'APPROVED' ? 'badge badge--success' : 'badge'}>
          {t(`stockCount.status${detail.status}`)}
        </span>
      </div>
      <p className="field-hint">
        {t(`stockCount.scope${detail.scopeType}`)}
        {detail.frozenAt ? ` · ${t('stockCount.frozenAtLabel')} ${new Date(detail.frozenAt).toLocaleString()}` : ''}
      </p>

      {(detail.status === 'DRAFT' || detail.status === 'IN_PROGRESS') && (
        <EntryMode countId={countId} detail={detail} />
      )}
      {detail.status === 'PENDING_APPROVAL' && <ApprovalMode countId={countId} />}
      {(detail.status === 'APPROVED' || detail.status === 'REJECTED' || detail.status === 'CANCELLED') && (
        <ReadOnlySummary detail={detail} />
      )}
    </div>
  );
}

// =============================================================================
// 录入模式（针对 EMPLOYEE 优化，S13：大字号、一次一行、数字键盘友好）
// =============================================================================
function EntryMode({
  countId,
  detail,
}: {
  readonly countId: string;
  readonly detail: StockCountDetail;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showAddLine, setShowAddLine] = useState(false);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['stock-counts', 'detail', countId] });
    void queryClient.invalidateQueries({ queryKey: ['stock-counts', 'list'] });
  };

  const submitMutation = useMutation({
    mutationFn: () => apiFetch(`/stock-counts/${countId}/submit`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const cancelMutation = useMutation({
    mutationFn: () => apiFetch(`/stock-counts/${countId}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const pendingCount = detail.lines.filter((l) => l.lineStatus === 'PENDING').length;
  const allDone = pendingCount === 0 && detail.lines.length > 0;

  return (
    <div>
      <div className="row" style={{ gap: 'var(--space-3)', margin: 'var(--space-3) 0' }}>
        <span>{t('stockCount.progressFraction', { counted: detail.lines.length - pendingCount, total: detail.lines.length })}</span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowAddLine(true)}>
          {t('stockCount.addUnlistedLine')}
        </button>
      </div>

      {detail.lines.length === 0 && (
        <div className="empty-state">
          <p>{t('stockCount.noLinesYet')}</p>
        </div>
      )}

      {detail.lines.map((line) => (
        <EntryLineCard key={line.id} countId={countId} line={line} showSystemQty={detail.showSystemQty} onDone={invalidate} />
      ))}

      <ErrorLine error={submitMutation.error} />
      <ErrorLine error={cancelMutation.error} />
      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!allDone || submitMutation.isPending}
          onClick={() => submitMutation.mutate()}
        >
          {submitMutation.isPending ? t('app.loading') : t('stockCount.submitButton')}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={cancelMutation.isPending}
          onClick={() => cancelMutation.mutate()}
        >
          {t('stockCount.cancelButton')}
        </button>
      </div>

      {showAddLine && (
        <AddUnlistedLineModal
          countId={countId}
          onClose={() => setShowAddLine(false)}
          onAdded={() => {
            setShowAddLine(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function EntryLineCard({
  countId,
  line,
  showSystemQty,
  onDone,
}: {
  readonly countId: string;
  readonly line: StockCountLineItem;
  readonly showSystemQty: boolean;
  readonly onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [qty, setQty] = useState(line.countedQtyBase ?? '');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [noExpiration, setNoExpiration] = useState(true);
  const [expirationDate, setExpirationDate] = useState('');

  const { data: product } = useQuery({
    queryKey: ['stock-counts', 'product-units', line.productId],
    queryFn: () => apiFetch<ProductDetail>(`/products/${line.productId}`),
  });
  const activeUnits = (product?.units ?? []).filter((u) => !u.archivedAt);
  const effectiveUnitId = unitId ?? activeUnits.find((u) => u.isBase)?.uomId ?? activeUnits[0]?.uomId ?? null;

  const countMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/stock-counts/${countId}/lines/${line.id}/count`, {
        method: 'POST',
        body: {
          quantity: { value: qty.trim(), unitId: effectiveUnitId },
          newBatchExpiration: line.batchId
            ? undefined
            : noExpiration
              ? { noExpiration: true }
              : { expirationDate },
        },
      }),
    onSuccess: onDone,
  });
  const skipMutation = useMutation({
    mutationFn: () => apiFetch(`/stock-counts/${countId}/lines/${line.id}/skip`, { method: 'POST' }),
    onSuccess: onDone,
  });

  const done = line.lineStatus !== 'PENDING';

  return (
    <div
      className="card"
      style={{
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
        opacity: done ? 0.7 : 1,
      }}
    >
      <div className="row row--between">
        <strong style={{ fontSize: 'var(--text-lg)' }}>{line.productName}</strong>
        <span className={done ? 'badge badge--success' : 'badge'}>{t(`stockCount.lineStatus${line.lineStatus}`)}</span>
      </div>
      <div className="field-hint">
        {line.locationLabel} {line.batchNo ? `· ${line.batchNo}` : `· ${t('stockCount.unlistedBatch')}`}
      </div>
      {showSystemQty && line.systemQtyBase !== null && (
        <div className="field-hint">{t('stockCount.systemQtyLabel')}: {line.systemQtyBase}</div>
      )}

      {!done && (
        <>
          <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)', alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: '200px' }}>
              <span style={{ fontSize: 'var(--text-lg)' }}>{t('stockCount.countedQtyLabel')}</span>
              <input
                inputMode="decimal"
                style={{ fontSize: 'var(--text-xl)', padding: 'var(--space-3)' }}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </label>
            {activeUnits.length > 1 && (
              <label className="field" style={{ maxWidth: '140px' }}>
                <span>{t('stockCount.unitLabel')}</span>
                <select value={effectiveUnitId ?? ''} onChange={(e) => setUnitId(e.target.value)}>
                  {activeUnits.map((u) => (
                    <option key={u.uomId} value={u.uomId}>
                      {u.uomCode}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {!line.batchId && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <label className="row" style={{ gap: 'var(--space-2)' }}>
                <input type="checkbox" checked={noExpiration} onChange={(e) => setNoExpiration(e.target.checked)} />
                <span>{t('stockCount.noExpirationLabel')}</span>
              </label>
              {!noExpiration && (
                <input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} />
              )}
            </div>
          )}
          <ErrorLine error={countMutation.error} />
          <ErrorLine error={skipMutation.error} />
          <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={countMutation.isPending || !qty.trim() || !effectiveUnitId}
              onClick={() => countMutation.mutate()}
            >
              {t('stockCount.confirmCountButton')}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={skipMutation.isPending}
              onClick={() => skipMutation.mutate()}
            >
              {t('stockCount.skipButton')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AddUnlistedLineModal({
  countId,
  onClose,
  onAdded,
}: {
  readonly countId: string;
  readonly onClose: () => void;
  readonly onAdded: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [product, setProduct] = useState<ComboboxOption | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/stock-counts/${countId}/lines`, {
        method: 'POST',
        body: { productId: product?.id, locationId },
      }),
    onSuccess: onAdded,
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('stockCount.addUnlistedLine')}</h2>
        <label className="field">
          <span>{t('orders.productLabel')}</span>
          <Combobox
            value={product}
            onChange={setProduct}
            fetchOptions={searchProducts}
            placeholder={t('stockCount.pickProduct')}
          />
        </label>
        <label className="field">
          <span>{t('stockCount.locationLabel')}</span>
          <LocationTreeSelect warehouseId={getCurrentWarehouseId()!} value={locationId} onChange={setLocationId} />
        </label>
        <ErrorLine error={addMutation.error} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={addMutation.isPending || !product || !locationId}
            onClick={() => addMutation.mutate()}
          >
            {addMutation.isPending ? t('app.loading') : t('app.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 审批模式（D-08 的核心）
// =============================================================================
function ApprovalMode({ countId }: { readonly countId: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canApprove = useCan(PERMISSIONS.STOCKCOUNT_APPROVE);
  const [decisions, setDecisions] = useState<Record<string, 'ADJUST' | 'RECOUNT'>>({});
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  const { data: review, isPending } = useQuery({
    queryKey: ['stock-counts', 'review', countId],
    queryFn: () => apiFetch<StockCountReview>(`/stock-counts/${countId}/review`),
    enabled: canApprove,
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/stock-counts/${countId}/approve`, {
        method: 'POST',
        body: { decisions: Object.entries(decisions).map(([lineId, action]) => ({ lineId, action })) },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      navigate(`/stock-counts/${countId}`);
    },
  });
  const rejectMutation = useMutation({
    mutationFn: () => apiFetch(`/stock-counts/${countId}/reject`, { method: 'POST', body: { reason: rejectReason } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      navigate(`/stock-counts/${countId}`);
    },
  });

  if (!canApprove) {
    return <p className="field-hint">{t('stockCount.pendingApprovalReadOnly')}</p>;
  }
  if (isPending || !review) return <SkeletonRows rows={4} />;

  const staleLines = review.lines.filter((l) => l.isStale);
  const nonStaleDiffLines = review.lines.filter((l) => !l.isStale && l.countedQtyBase !== l.systemQtyBaseCurrent);
  const missingDecisions = staleLines.filter((l) => !decisions[l.lineId]);

  return (
    <div>
      <p style={{ marginTop: 'var(--space-3)' }}>
        {t('stockCount.reviewSummary', { total: review.lines.length, diff: review.diffLineCount, stale: review.staleLineCount })}
      </p>

      {nonStaleDiffLines.length > 0 && (
        <>
          <h3>{t('stockCount.nonStaleDiffTitle')}</h3>
          {nonStaleDiffLines.map((l) => (
            <div key={l.lineId} className="field-hint" style={{ padding: 'var(--space-1) 0' }}>
              {t('stockCount.diffLine', { system: l.systemQtyBaseCurrent, counted: l.countedQtyBase })}
            </div>
          ))}
        </>
      )}

      {staleLines.length > 0 && (
        <>
          <h3 style={{ color: 'var(--color-warning)' }}>{t('stockCount.staleSectionTitle')}</h3>
          {staleLines.map((l) => (
            <div key={l.lineId} className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
              <div className="badge badge--warn">{t('stockCount.staleWarningBadge')}</div>
              <p>
                {t('stockCount.staleThreeNumbers', {
                  frozen: l.systemQtyBaseFrozen,
                  current: l.systemQtyBaseCurrent,
                  counted: l.countedQtyBase,
                })}
              </p>
              <div className="field-hint">
                {l.recentMovements.map((m) => (
                  <div key={m.movementId}>
                    {fmt.format(new Date(m.performedAt))} · {m.docNo} · {m.direction === 'IN' ? '+' : '-'}
                    {m.qtyBase}
                  </div>
                ))}
              </div>
              <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                <button
                  type="button"
                  className={decisions[l.lineId] === 'ADJUST' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                  onClick={() => setDecisions((d) => ({ ...d, [l.lineId]: 'ADJUST' }))}
                >
                  {t('stockCount.adjustToActualButton')}
                </button>
                <button
                  type="button"
                  className={decisions[l.lineId] === 'RECOUNT' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                  onClick={() => setDecisions((d) => ({ ...d, [l.lineId]: 'RECOUNT' }))}
                >
                  {t('stockCount.requestRecountButton')}
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      <ErrorLine error={approveMutation.error} />
      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={approveMutation.isPending || missingDecisions.length > 0}
          title={missingDecisions.length > 0 ? t('stockCount.staleDecisionsRequiredHint') ?? undefined : undefined}
          onClick={() => approveMutation.mutate()}
        >
          {approveMutation.isPending ? t('app.loading') : t('stockCount.approveButton')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setShowReject(true)}>
          {t('stockCount.rejectButton')}
        </button>
      </div>

      {showReject && (
        <div className="modal-overlay" role="presentation" onClick={() => setShowReject(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h2>{t('stockCount.rejectButton')}</h2>
            <label className="field">
              <span>{t('stockCount.rejectReasonLabel')}</span>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} />
            </label>
            <ErrorLine error={rejectMutation.error} />
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowReject(false)}>
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                onClick={() => rejectMutation.mutate()}
              >
                {rejectMutation.isPending ? t('app.loading') : t('stockCount.rejectConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// 只读小结
// =============================================================================
function ReadOnlySummary({ detail }: { readonly detail: StockCountDetail }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      {detail.status === 'REJECTED' && detail.rejectedReason && (
        <p className="field-error">
          {t('stockCount.rejectReasonLabel')}: {detail.rejectedReason}
        </p>
      )}
      {detail.adjustmentMovementId && (
        <p className="field-hint">
          {t('stockCount.adjustmentMovementLabel')}:{' '}
          <Link to={`/movements`} className="link">{detail.adjustmentMovementId}</Link>
        </p>
      )}
      <div className="table" style={{ display: 'block' }}>
        {detail.lines.map((l) => (
          <div key={l.id} className="tree__row" style={{ padding: 'var(--space-2) 0' }}>
            <span style={{ flex: 2 }}>{l.productName}</span>
            <span style={{ width: '140px' }}>{l.locationLabel}</span>
            <span style={{ width: '100px' }}>{l.systemQtyBase ?? '—'}</span>
            <span style={{ width: '100px' }}>{l.countedQtyBase ?? '—'}</span>
            <span style={{ width: '100px' }}>{l.varianceBase ?? '—'}</span>
            <span style={{ width: '120px' }}>{t(`stockCount.lineStatus${l.lineStatus}`)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
